import { useMemo, useRef, useEffect, useCallback } from 'react'
import { marked, type MarkedOptions } from 'marked'
import hljs from 'highlight.js'
import DOMPurify from 'dompurify'
import styles from './markdown.module.css'

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function createRenderer() {
  return {
    code(token: { text: string; lang?: string }) {
      const { text, lang } = token
      let codeHtml: string
      const langLabel = lang || ''

      if (lang && hljs.getLanguage(lang)) {
        try {
          const highlighted = hljs.highlight(text, { language: lang, ignoreIllegals: true }).value
          codeHtml = `<code class="hljs language-${lang}">${highlighted}</code>`
        } catch {
          codeHtml = `<code>${escapeHtml(text)}</code>`
        }
      } else {
        codeHtml = `<code>${escapeHtml(text)}</code>`
      }

      const escapedCode = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')

      return `
        <div class="${styles.codeBlockWrapper}">
          <div class="${styles.codeBlockHeader}">
            <span class="${styles.codeBlockLang}">${langLabel}</span>
            <button class="${styles.copyButton}" data-copy-code="${escapedCode}" type="button" aria-label="Copy code">
              <svg class="${styles.copyIcon}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
              <svg class="${styles.checkIcon}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span class="${styles.copyText}">Copy</span>
            </button>
          </div>
          <pre class="${styles.codeBlock}">${codeHtml}</pre>
        </div>
      `
    },
    codespan(token: { text: string }) {
      return `<code class="${styles.inlineCode}">${escapeHtml(token.text)}</code>`
    },
    link(token: { href: string; title?: string | null; text: string }) {
      const { href, title, text } = token
      const titleAttr = title ? ` title="${title}"` : ''
      return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
    },
  }
}

function fixIncompleteMarkdown(content: string): string {
  let fixed = content
  const codeBlockMatches = fixed.match(/```/g)
  if (codeBlockMatches && codeBlockMatches.length % 2 !== 0) {
    fixed += '\n```'
  }
  const withoutCodeBlocks = fixed.replace(/```[\s\S]*?```/g, '')
  const inlineCodeMatches = withoutCodeBlocks.match(/`/g)
  if (inlineCodeMatches && inlineCodeMatches.length % 2 !== 0) {
    fixed += '`'
  }
  return fixed
}

function parseMarkdown(content: string): string {
  const options: MarkedOptions = { gfm: true, breaks: true }
  marked.use({ renderer: createRenderer() })
  const rawHtml = marked.parse(content, options) as string
  return DOMPurify.sanitize(rawHtml, {
    ADD_ATTR: ['target', 'rel', 'data-copy-code'],
  })
}

interface MarkdownRendererProps {
  content: string
  isStreaming?: boolean
  className?: string
}

export function MarkdownRenderer({ content, isStreaming, className }: MarkdownRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const html = useMemo(() => {
    if (!content) return ''
    const processed = isStreaming ? fixIncompleteMarkdown(content) : content
    return parseMarkdown(processed)
  }, [content, isStreaming])

  const handleCopyClick = useCallback(async (e: MouseEvent) => {
    const button = (e.target as HTMLElement).closest(`.${styles.copyButton}`) as HTMLButtonElement
    if (!button) return
    const code = button.getAttribute('data-copy-code')
    if (!code) return
    try {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = code
      await navigator.clipboard.writeText(textarea.value)
      button.classList.add(styles.copied)
      setTimeout(() => button.classList.remove(styles.copied), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    container.addEventListener('click', handleCopyClick)
    return () => container.removeEventListener('click', handleCopyClick)
  }, [html, handleCopyClick])

  if (!content) return null

  return (
    <div ref={containerRef} className={`${styles.markdown} ${className || ''}`}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
      {isStreaming && <span className={styles.streamingCursor} aria-hidden="true" />}
    </div>
  )
}
